import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import DesignPicker, { type PickerRecipe } from '@/components/DesignPicker';
import {
  recommendSlots, applyDesignsToTemplate, classifyArchetype, roleLabel,
  type Template, type Slot, type RecipeLite,
} from '@/lib/design-recommend';

type Product = { id: string; style: string; position: string; color: string; category: string; displayName: string };
type Recipe = PickerRecipe & { category?: string; defaultPrintArea?: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;          // the sport-artwork template being edited
  productTemplate: Template | null;   // the selected products template (canonical styles + colors)
  products: Product[];                // store-preview products; already real & renderable
  recipes: Recipe[];                  // full recipe list (DesignPicker scopes by sport)
  saving: boolean;
  onApply: (updated: Template) => void | Promise<void>;
};

// Canonical style number (matching the product template + existing sport-template
// entries) for a store-preview product whose style is lower-cased from its doc id.
function canonicalStyle(productTemplate: Template | null, style: string): string {
  const hit = (productTemplate?.styles || []).find((s) => String(s.styleNumber).toUpperCase() === style.toUpperCase());
  return hit ? hit.styleNumber : style.toUpperCase();
}

export default function AddArtworkDialog({ open, onOpenChange, template, productTemplate, products, recipes, saving, onApply }: Props) {
  const [recipeId, setRecipeId] = useState<string>('');

  useEffect(() => { if (open) setRecipeId(''); }, [open]);

  const chosen: Recipe | null = useMemo(() => recipes.find((r) => r.id === recipeId) || null, [recipes, recipeId]);

  // One slot per distinct (canonical style, position). store-preview-products only
  // emits real, renderable products, so every slot is safe to assign.
  const slots: Slot[] = useMemo(() => {
    const seen = new Set<string>();
    const out: Slot[] = [];
    for (const p of products) {
      const styleNumber = canonicalStyle(productTemplate, p.style);
      const key = `${styleNumber.toUpperCase()}|${p.position}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ styleNumber, category: p.category, positionCode: p.position, displayName: p.displayName });
    }
    return out;
  }, [products, productTemplate]);

  const recs = useMemo(
    () => (chosen ? recommendSlots(chosen as RecipeLite & { id: string }, slots, template?.styles || []) : []),
    [chosen, slots, template],
  );
  const visible = recs.filter((r) => !r.isExactDup); // exact dups are no-ops; hide them

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const slotKey = (s: Slot) => `${s.styleNumber}|${s.positionCode}`;
  // Re-derive the pre-checked set whenever the recipe OR the available slots change
  // (slotSig changes if products/template change while the dialog stays open).
  const slotSig = useMemo(() => slots.map(slotKey).sort().join(','), [slots]);
  useEffect(() => {
    setChecked(new Set(visible.filter((r) => r.recommended).map((r) => slotKey(r.slot))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId, slotSig]);

  const groups = useMemo(() => {
    const m: Record<string, typeof visible> = {};
    for (const r of visible) (m[r.slot.category] = m[r.slot.category] || []).push(r);
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  const archetype = chosen ? classifyArchetype(chosen as RecipeLite) : null;
  const checkedCount = checked.size;
  const garmentCount = new Set(visible.filter((r) => checked.has(slotKey(r.slot))).map((r) => r.slot.styleNumber)).size;

  function toggle(s: Slot) {
    const k = slotKey(s);
    setChecked((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  }

  async function apply() {
    if (!template || !chosen) return;
    const additions = visible.filter((r) => checked.has(slotKey(r.slot))).map((r) => ({ slot: r.slot, role: r.role }));
    if (!additions.length) return;
    const updated = applyDesignsToTemplate(template, chosen.id, additions, productTemplate);
    await onApply(updated);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add artwork to "{template?.name || template?.id}"</DialogTitle>
          <DialogDescription>
            Changes this sport template for <strong>all future stores of this sport</strong>. Pick a design, then confirm the recommended products.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <DesignPicker
              recipes={recipes}
              value={recipeId}
              onChange={(v) => setRecipeId(v as string)}
              cols={3}
              maxHeight={420}
              defaultSport={template?.sport}
            />
          </div>

          <div className="min-h-[300px]">
            {!chosen ? (
              <div className="flex h-full items-center justify-center text-[12px] text-muted">Pick a design to see recommended products.</div>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[13px] font-semibold">{chosen.name || chosen.id}</span>
                  {archetype && <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">{roleLabel[archetype]}</span>}
                </div>
                <div className="mb-2 text-[11px] text-muted">Adds {checkedCount} product{checkedCount === 1 ? '' : 's'} across {garmentCount} garment{garmentCount === 1 ? '' : 's'}.</div>
                <div className="max-h-[330px] space-y-3 overflow-y-auto pr-1">
                  {groups.length === 0 && <div className="text-[11px] text-muted">No products available for this template.</div>}
                  {groups.map(([cat, rows]) => (
                    <div key={cat}>
                      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-subtle">{cat}</div>
                      <div className="space-y-1">
                        {rows.map((r) => {
                          const k = slotKey(r.slot);
                          return (
                            <label key={k} className="flex items-center gap-2 text-[12px]">
                              <input type="checkbox" checked={checked.has(k)} onChange={() => toggle(r.slot)} />
                              <span className="flex-1 truncate">{r.slot.displayName}</span>
                              <span className="rounded-sm bg-bg px-1 text-[9px] uppercase text-subtle">{r.slot.positionCode}</span>
                              {r.recommended && <span className="text-[9px] text-primary/80">recommended</span>}
                              {r.alreadyHasDesign && <span className="text-[9px] text-subtle" title="already has a design here">has design</span>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={saving || !chosen || checked.size === 0} onClick={() => { void apply(); }}>
            {saving ? 'Saving…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
